import { ObjectId } from "bson";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import useClickOutside from "../../../../hooks/useClickOutside";
import { useModifiedPolling } from "../../../../hooks/useModifiedPolling";
import Pagination from "../../../../parts/pagination";
import { isTemplateProcessing } from "../../../../utils/meal-planner-helper";
import { restoreScrollAfterNavigation } from "../../../../utils/preserve-scroll-on-navigation";
import { MealPlanCardDetailed } from "./MealPlanCardDetailed";
import { MealPlanCardSimplified } from "./MealPlanCardSimplified";
/**
 * Function to apply the custom height of the cards
 * @param {*} templates - Array of templates to calculate height from
 * @param {string} planMode - "detailed" or "simplified"
 * @returns
 */
const getHeightClass = (templates, planMode) => {
  let maxLength = 0;
  for (const t of templates) {
    const title = t.title || "";
    const description = t.description || "";
    maxLength = Math.max(maxLength, (title + description).length);
  }

  if (planMode === "simplified") {
    // Simplified cards have smaller heights
    if (maxLength > 150) return "h-[9.1rem]";
    if (maxLength > 120) return "h-29.5";
    if (maxLength > 107) return "h-[6.7rem]";
    return "h-26";
  }

  // Detailed cards keep original heights
  if (maxLength > 120) return "h-37";
  if (maxLength > 107) return "h-36";
  return "h-33";
};

const MealPlanTemplates = ({ locals, templates }) => {
  const { t } = useTranslation("coaching-portal");
  const [acknowledgedTemplateIds, setAcknowledgedTemplateIds] = useState(
    () => new Set()
  );
  const { query, mealPlanCount = 1, activeUser } = locals;
  const { sort = "createdDate", modal, planMode = "detailed" } = query;
  const referAsClients = Boolean(activeUser?.referAsClients);
  const dropdownRef = useRef(null);
  const prevModalRef = useRef(modal);

  const footerListIconBase = useMemo(
    () => [
      {
        id: "preview-template",
        src: "/img/new-icons/eye.svg",
        alt: "preview",
        message: t("allTemplates.mealPlanTemplates.preview"),
        url: "/coaching-portal/templates/edit-meal-plan",
      },
      {
        id: "copy-meal-plan-to",
        src: "/img/new-icons/copy-to.svg",
        alt: "fetch",
        message: t("allTemplates.mealPlanTemplates.copyTemplateTo"),
        url: "/actions/refresh?modal[type]=copy-meal-plan-to",
      },
      {
        id: "copy-meal-plan-from",
        src: "/img/new-icons/copy-from.svg",
        alt: "fetch",
        message: t("allTemplates.mealPlanTemplates.copyTemplateFrom"),
        url: "/actions/refresh?modal[type]=copy-meal-plan-from",
      },
      {
        id: "duplicate-template",
        src: "/img/new-icons/duplicate.svg",
        alt: "duplicate",
        message: t("allTemplates.mealPlanTemplates.duplicateTemplate"),
        url: ``,
      },
      {
        id: "meal-plan-settings",
        src: "/img/new-icons/settings.svg",
        alt: "view",
        message: t("allTemplates.mealPlanTemplates.settings"),
        url: "/actions/refresh?modal[type]=handle-meal-plan",
      },
      {
        id: "delete-template",
        src: "/img/new-icons/red-trash.svg",
        alt: "delete",
        message: t("allTemplates.mealPlanTemplates.delete"),
        url: ``,
      },
    ],
    [t]
  );

  const cardListIcon = useMemo(
    () => [
      {
        src: "/img/new-icons/duplicate.svg",
        alt: "duplicate",
        message: t("allTemplates.mealPlanTemplates.duplicateWeek"),
        id: "duplicate-week",
        url: `/actions/meal-planner/duplicate-week-meal-plan`,
      },
      {
        src: "/img/new-icons/export.svg",
        alt: "duplicate",
        message: t("allTemplates.mealPlanTemplates.groceryList"),
        id: "grocery-list",
        url: `/actions/meal-planner/generate-grocery-list`,
      },
      {
        src: "/img/new-icons/red-trash.svg",
        alt: "delete",
        message: t("allTemplates.mealPlanTemplates.deleteWeek"),
        id: "delete-week",
        url: ``,
      },
    ],
    [t]
  );

  const sortFunctions = {
    name: (a, b) => {
      const aIsProcessing = isTemplateProcessing(a);
      const bIsProcessing = isTemplateProcessing(b);
      if (aIsProcessing && !bIsProcessing) return -1;
      if (!aIsProcessing && bIsProcessing) return 1;
      return a.title.localeCompare(b.title);
    },
    nameDescending: (a, b) => {
      const aIsProcessing = isTemplateProcessing(a);
      const bIsProcessing = isTemplateProcessing(b);
      if (aIsProcessing && !bIsProcessing) return -1;
      if (!aIsProcessing && bIsProcessing) return 1;
      return b.title.localeCompare(a.title);
    },
    createdDate: (a, b) => {
      const aIsProcessing = isTemplateProcessing(a);
      const bIsProcessing = isTemplateProcessing(b);
      if (aIsProcessing && !bIsProcessing) return -1;
      if (!aIsProcessing && bIsProcessing) return 1;
      return (
        new ObjectId(b._id).getTimestamp() - new ObjectId(a._id).getTimestamp()
      );
    },
  };
  // Filter by planMode + develop rules and sort
  const sortedTemplates = [...templates]
    .filter((template) => {
      const templatePlanMode = template?.planMode || "detailed";
      if (templatePlanMode !== planMode) return false;

      const generation = template?.generation;
      const status = generation?.status;
      const isCanceled = status === "canceled" || status === "cancelled";
      if (isCanceled) return false;

      // For detailed: only show if processing or has weeks
      // For simplified: show all (no restrictive filter)
      if (templatePlanMode === "detailed") {
        const isProcessing = isTemplateProcessing(template);
        const hasWeeks =
          Array.isArray(template?.weeks) && template.weeks.length > 0;
        return isProcessing || hasWeeks;
      }

      // Simplified: show all non-canceled templates
      return true;
    })
    .sort(sortFunctions[sort] || sortFunctions.createdDate);

  // Filter templates by type for height calculation
  const detailedTemplates = templates.filter(
    (t) => (t?.planMode || "detailed") === "detailed"
  );
  const simplifiedTemplates = templates.filter(
    (t) => (t?.planMode || "detailed") === "simplified"
  );

  const heightClassDetailed = getHeightClass(detailedTemplates, "detailed");
  const heightClassSimplified = getHeightClass(
    simplifiedTemplates,
    "simplified"
  );

  const [openOptions, setOpenOptions] = useState(null);

  const footerListIconDetailed = footerListIconBase;
  const footerListIconSimplified = footerListIconBase.filter(
    ({ id }) => id !== "copy-meal-plan-to" && id !== "copy-meal-plan-from"
  );

  useEffect(() => {
    if (!Array.isArray(templates) || templates.length === 0) {
      return;
    }

    const toAcknowledge = templates.filter((template) => {
      const generation = template?.generation;
      if (!generation || !template?._id) {
        return false;
      }
      const isDone =
        generation.status === "success" || generation.status === "failed";
      return (
        isDone &&
        generation.isChecked === false &&
        !acknowledgedTemplateIds.has(String(template._id))
      );
    });

    if (toAcknowledge.length === 0) {
      return;
    }
    toAcknowledge.forEach((template) => {
      submit(`/actions/meal-planner/generate-meal-plan-status`, {
        method: "POST",
        body: {
          mealPlanId: String(template._id),
          status: template.generation.status,
          isChecked: false,
        },
      });
    });

    setAcknowledgedTemplateIds((prev) => {
      const next = new Set(prev);
      toAcknowledge.forEach((template) => next.add(String(template._id)));
      return next;
    });
  }, [templates, acknowledgedTemplateIds]);
  // Check if any meal plan has pending generation status
  const hasPendingGeneration = useMemo(() => {
    if (!Array.isArray(templates) || templates.length === 0) {
      return false;
    }
    return templates.some((template) => isTemplateProcessing(template));
  }, [templates]);

  // Add polling for meal plan updates only if there are pending operations
  useModifiedPolling({
    interval: 5000,
    enabled: true,
    field: "meal-plan",
    shouldPoll: () => hasPendingGeneration,
  });

  const IDsThatPreserveScroll = [
    "copy-meal-plan-to",
    "copy-meal-plan-from",
    "meal-plan-settings",
  ];

  /**
   * Function to show the details options in the correct position
   * @param {*} e
   * @param {*} templateId
   * @param {*} weekIndex
   */
  const handleOptions = (e, templateId, weekIndex) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();

    setOpenOptions((prev) =>
      prev?.templateId === templateId && prev?.weekIndex === weekIndex
        ? null
        : {
            templateId,
            weekIndex,
            position: {
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX,
            },
          }
    );
  };

  /**
   * Close the details options clicking outside
   */
  useClickOutside(
    dropdownRef,
    () => {
      setTimeout(() => {
        setOpenOptions(null);
      }, 100);
    },
    openOptions
  );

  /**
   * Function to delete or duplicate a meal plan
   * @param {*} templateId
   */
  const handleTemplateAction = (templateId, id) => {
    const actionMap = {
      "delete-template": "delete",
      "duplicate-template": "duplicate",
    };

    const action = actionMap[id];
    const needsConfirm = action === "delete";

    if (
      !needsConfirm ||
      (process.browser &&
        confirm(t("allTemplates.mealPlanTemplates.confirmDeleteMealPlan")))
    ) {
      // Include planMode and page in URL if they exist
      const { page } = query;
      const planModeParam = planMode ? `&planMode=${planMode}` : "";
      const pageParam = page ? `&page=${page}` : "";
      submit(
        `/actions/meal-planner/${action}-meal-plan?mealPlanId=${templateId}&proxyAthlete=${null}&nutritionPlanId=${null}${planModeParam}${pageParam}`,
        { method: "POST" }
      );
    }
  };

  /**
   * Function to delete a week from the template
   * @param {*} templateId
   * @param {*} week
   */
  const handleDeleteWeek = (templateId, week) => {
    if (
      process.browser &&
      confirm(t("allTemplates.mealPlanTemplates.confirmDeleteWeek"))
    ) {
      submit(
        `/actions/meal-planner/delete-week-meal-plan?mealPlanId=${templateId}&week=${week}`,
        {
          method: "POST",
        }
      );
    }
  };

  /**
   * Function to duplicate a week from the template
   * @param {*} templateId
   * @param {*} week
   */
  const handleDuplicateWeek = (templateId, week) => {
    submit(
      `/actions/meal-planner/duplicate-week-meal-plan?mealPlanId=${templateId}&week=${week}`,
      {
        method: "POST",
      }
    );
  };

  /**
   * Function to add a week to a meal plan
   * @param {*} templateId
   */
  const handleAddWeek = (templateId) => {
    const body = {};
    const url = `/actions/meal-planner/add-week-meal-plan?mealPlanId=${templateId}&proxyAthlete=${undefined}`;

    submit(url, {
      method: "POST",
      body,
    });
  };

  /**
   * Effect to scroll to the last selected card
   * @param {*} templateId
   */
  useEffect(() => {
    const prevModal = prevModalRef.current;
    const currentModal = modal;

    //know if the modal is closed
    const modalClosed = prevModal && !currentModal;

    if (modalClosed) {
      restoreScrollAfterNavigation();
    }

    // Update reference
    prevModalRef.current = currentModal;
  }, [modal]);

  return (
    <section className="meal-plan-templates-section">
      {sortedTemplates.length > 0 ? (
        <>
          <div className="meal-plan-templates">
            {sortedTemplates.map((template) => {
              // All templates in sortedTemplates are already filtered by planMode from query
              const CardComponent =
                planMode === "simplified"
                  ? MealPlanCardSimplified
                  : MealPlanCardDetailed;
              const footerListIcon =
                planMode === "simplified"
                  ? footerListIconSimplified
                  : footerListIconDetailed;
              const heightClass =
                planMode === "simplified"
                  ? heightClassSimplified
                  : heightClassDetailed;

              const cardProps = {
                template,
                heightClass,
                dropdownRef,
                openOptions,
                setOpenOptions,
                handleOptions,
                handleDeleteWeek,
                handleDuplicateWeek,
                handleTemplateAction,
                cardListIcon,
                footerListIcon,
                IDsThatPreserveScroll,
              };

              // Only pass handleAddWeek to Detailed cards
              if (planMode === "detailed") {
                cardProps.handleAddWeek = handleAddWeek;
              }

              return <CardComponent key={template._id} {...cardProps} />;
            })}
          </div>
          <Pagination total={mealPlanCount} pageSize={40} />
        </>
      ) : (
        <div className="no-programs-section">
          <p>{t("allTemplates.noTemplatesAdded")}</p>
          <span>
            {t("allTemplates.mealPlanTemplates.noTemplatesMessage", {
              planType: planMode === "simplified" ? t("allTemplates.mealPlanTemplates.singleDay") : t("allTemplates.mealPlanTemplates.multiDay"),
              role: referAsClients ? t("athletes.roleClients") : t("athletes.roleAthletes"),
            })}
          </span>
          <a
            role="button"
            data-button-type="primary"
            href={`/actions/refresh/?modal[type]=handle-meal-plan&planMode=${planMode}`}
          >
            {t("allTemplates.addTemplate")}
          </a>
        </div>
      )}
    </section>
  );
};

MealPlanTemplates.propTypes = {
  locals: PropTypes.object,
  templates: PropTypes.array,
};

export default MealPlanTemplates;
